import React, { useEffect, useState, useCallback } from 'react';
import { useAppSelector } from '../../store/hooks';
import { fetchAdminUsers, unblacklistUser, fetchBlacklistedIPs, addBlacklistedIP, removeBlacklistedIP } from '../../api/admin';
import { AdminDataTable, AdminConfirmModal, Column } from '../../components/admin';
import { FiPlus } from 'react-icons/fi';

const AdminBlacklist: React.FC = () => {
  const colors = useAppSelector((state) => state.theme.theme);
  const [activeTab, setActiveTab] = useState<'users' | 'ips'>('users');

  // Users
  const [blockedUsers, setBlockedUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [unblockTarget, setUnblockTarget] = useState<any>(null);

  // IPs
  const [blockedIPs, setBlockedIPs] = useState<any[]>([]);
  const [ipsLoading, setIpsLoading] = useState(true);
  const [newIP, setNewIP] = useState('');
  const [newIPReason, setNewIPReason] = useState('');
  const [removeTarget, setRemoveTarget] = useState<any>(null);

  const loadUsers = useCallback(() => {
    setUsersLoading(true);
    fetchAdminUsers({ page: 1, page_size: 100, filter: 'blacklisted' })
      .then((res) => setBlockedUsers(res.data.results))
      .catch(() => {})
      .finally(() => setUsersLoading(false));
  }, []);

  const loadIPs = useCallback(() => {
    setIpsLoading(true);
    fetchBlacklistedIPs()
      .then((res) => setBlockedIPs(res.data.results))
      .catch(() => {})
      .finally(() => setIpsLoading(false));
  }, []);

  useEffect(() => { loadUsers(); loadIPs(); }, [loadUsers, loadIPs]);

  const handleUnblock = async () => {
    if (!unblockTarget) return;
    try {
      await unblacklistUser(unblockTarget.id);
      setUnblockTarget(null);
      loadUsers();
    } catch {}
  };

  const handleAddIP = async () => {
    if (!newIP) return;
    try {
      await addBlacklistedIP(newIP, newIPReason);
      setNewIP('');
      setNewIPReason('');
      loadIPs();
    } catch {}
  };

  const handleRemoveIP = async () => {
    if (!removeTarget) return;
    try {
      await removeBlacklistedIP(removeTarget.id);
      setRemoveTarget(null);
      loadIPs();
    } catch {}
  };

  const userColumns: Column[] = [
    { key: 'id', header: 'ID', width: '60px' },
    { key: 'name', header: 'Name', render: (row) => row.name || '-' },
    { key: 'email', header: 'Email', render: (row) => row.email || '-' },
    { key: 'date_joined', header: 'Joined', render: (row) => row.date_joined?.split('T')[0] },
    {
      key: 'actions', header: '', width: '100px',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setUnblockTarget(row); }}
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ backgroundColor: '#10B98120', color: '#10B981' }}
        >
          Unblock
        </button>
      ),
    },
  ];

  const ipColumns: Column[] = [
    { key: 'id', header: 'ID', width: '60px' },
    { key: 'ip_address', header: 'IP Address' },
    { key: 'reason', header: 'Reason', render: (row) => row.reason || '-' },
    { key: 'created_at', header: 'Blocked On', render: (row) => row.created_at?.split('T')[0] },
    {
      key: 'actions', header: '', width: '100px',
      render: (row) => (
        <button
          onClick={(e) => { e.stopPropagation(); setRemoveTarget(row); }}
          className="px-2 py-1 rounded text-xs font-medium"
          style={{ backgroundColor: '#EF444420', color: '#EF4444' }}
        >
          Remove
        </button>
      ),
    },
  ];

  const tabs = ['users', 'ips'] as const;

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold mb-6" style={{ color: colors.textColor }}>Blacklist Management</h1>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 border-b" style={{ borderColor: colors.borderColor }}>
        {tabs.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className="px-4 py-2.5 text-sm font-medium capitalize"
            style={{
              color: activeTab === tab ? colors.primary : colors.grayScale500,
              borderBottom: activeTab === tab ? `2px solid ${colors.primary}` : '2px solid transparent',
            }}
          >
            {tab === 'users' ? 'Blocked Users' : 'Blocked IPs'}
          </button>
        ))}
      </div>

      {activeTab === 'users' && (
        <AdminDataTable columns={userColumns} data={blockedUsers} loading={usersLoading} emptyMessage="No blocked users" />
      )}

      {activeTab === 'ips' && (
        <>
          {/* Add IP form */}
          <div className="flex flex-wrap gap-3 mb-4">
            <input
              value={newIP}
              onChange={(e) => setNewIP(e.target.value)}
              placeholder="IP Address (e.g., 192.168.1.1)"
              className="px-3 py-2 rounded-xl text-sm outline-none"
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
            />
            <input
              value={newIPReason}
              onChange={(e) => setNewIPReason(e.target.value)}
              placeholder="Reason (optional)"
              className="px-3 py-2 rounded-xl text-sm outline-none flex-1 min-w-[150px]"
              style={{ backgroundColor: colors.inputBackground, border: `1px solid ${colors.borderColor}`, color: colors.textColor }}
            />
            <button
              onClick={handleAddIP}
              disabled={!newIP}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white disabled:opacity-50"
              style={{ backgroundColor: colors.primary }}
            >
              {React.createElement(FiPlus as any, { size: 16 })} Block IP
            </button>
          </div>
          <AdminDataTable columns={ipColumns} data={blockedIPs} loading={ipsLoading} emptyMessage="No blocked IPs" />
        </>
      )}

      {/* Modals */}
      {unblockTarget && (
        <AdminConfirmModal
          title="Unblock User"
          message={`Restore access for ${unblockTarget.name || unblockTarget.email || `User #${unblockTarget.id}`}?`}
          confirmLabel="Unblock"
          onConfirm={handleUnblock}
          onCancel={() => setUnblockTarget(null)}
        />
      )}
      {removeTarget && (
        <AdminConfirmModal
          title="Remove IP Block"
          message={`Unblock IP ${removeTarget.ip_address}?`}
          confirmLabel="Remove"
          onConfirm={handleRemoveIP}
          onCancel={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
};

export default AdminBlacklist;
